import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ── CSS global injecté ────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&family=Space+Grotesk:wght@700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { overflow-x: hidden; }

  @keyframes fadeUp  { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:translateY(0); } }
  @keyframes heroIn  { from { opacity:0; transform:translateY(40px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pulse2  { 0%,100% { opacity:0.4; transform:scale(1); } 50% { opacity:0.9; transform:scale(1.08); } }
  @keyframes spin360 { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
  @keyframes floatY  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-14px); } }
  @keyframes gridScroll { from { transform:translateY(0); } to { transform:translateY(80px); } }

  .teckia-landing { font-family:'DM Sans','Segoe UI',Arial,sans-serif; }
  .hero-title {
    font-family:'Space Grotesk',sans-serif;
    font-size: clamp(40px,8vw,84px);
    font-weight:800; line-height:1.05; letter-spacing:-2px;
    animation: heroIn 0.9s ease both;
  }
  .hero-sub {
    font-size: clamp(15px,2.5vw,19px);
    color:rgba(255,255,255,0.5); line-height:1.7;
    animation: heroIn 0.9s ease 0.2s both;
  }
  .gradient-text {
    background: linear-gradient(135deg,#00ffcc,#00d4aa 40%,#00836b);
    -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
  }
  .badge {
    display:inline-flex; align-items:center; gap:8px;
    background:rgba(0,212,170,0.12); border:1px solid rgba(0,212,170,0.3);
    color:#00d4aa; font-size:11px; font-weight:800; letter-spacing:1.2px;
    padding:8px 18px; border-radius:100px; text-transform:uppercase;
  }
  .cta-primary {
    display:inline-flex; align-items:center; gap:10px;
    background:linear-gradient(135deg,#00d4aa,#00836b);
    color:#fff; font-weight:800; font-size:16px;
    padding:16px 36px; border-radius:16px; border:none; cursor:pointer;
    text-decoration:none; transition:all 0.25s ease;
    box-shadow:0 8px 32px rgba(0,212,170,0.35);
    animation: heroIn 0.9s ease 0.4s both;
  }
  .cta-primary:hover { transform:translateY(-3px) scale(1.02); box-shadow:0 16px 48px rgba(0,212,170,0.5); }
  .cta-outline {
    display:inline-flex; align-items:center; gap:8px;
    color:rgba(255,255,255,0.55); font-size:15px; font-weight:600;
    padding:16px 24px; border-radius:16px;
    border:1px solid rgba(255,255,255,0.1);
    background:rgba(255,255,255,0.04); text-decoration:none;
    transition:all 0.25s ease; cursor:pointer;
    animation: heroIn 0.9s ease 0.5s both;
  }
  .cta-outline:hover { background:rgba(255,255,255,0.08); color:#fff; border-color:rgba(255,255,255,0.2); }
  .feat-card {
    background:rgba(255,255,255,0.03);
    border-radius:24px; padding:32px 28px;
    position:relative; overflow:hidden;
    animation: fadeUp 0.6s ease both;
    transition:transform 0.3s ease, box-shadow 0.3s ease;
    cursor:default;
  }
  .feat-card:hover { transform:translateY(-6px); }
  .step-row {
    display:flex; align-items:center; gap:28px;
    background:rgba(255,255,255,0.03);
    border-radius:20px; padding:24px 28px;
    animation: fadeUp 0.6s ease both;
    transition:all 0.3s ease;
    cursor:default;
  }
  .stat-card {
    text-align:center; padding:32px 20px;
    border-radius:24px; border:1px solid rgba(255,255,255,0.07);
    background:rgba(255,255,255,0.03);
  }
  .nav-link {
    color:rgba(255,255,255,0.5); font-size:14px; font-weight:600;
    text-decoration:none; transition:color 0.2s;
  }
  .nav-link:hover { color:#fff; }
  .footer-link {
    color:rgba(255,255,255,0.35); font-size:13px; font-weight:600;
    text-decoration:none; transition:color 0.2s;
  }
  .footer-link:hover { color:#00d4aa; }
  .hero-actions { display:flex; gap:14px; justify-content:center; flex-wrap:wrap; }

  @media(max-width:768px) {
    .feat-grid  { grid-template-columns:1fr !important; }
    .stats-grid { grid-template-columns:repeat(2,1fr) !important; }
    .hero-actions { flex-direction:column; align-items:stretch; }
    .cta-primary, .cta-outline { text-align:center; justify-content:center; }
    .nav-links { display:none !important; }
  }
`;

// ── Animated Counter ──────────────────────────────────────────────────────────
function Counter({ target, suffix }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !done.current) {
        done.current = true;
        const start = Date.now();
        const tick = () => {
          const p = Math.min((Date.now() - start) / 2000, 1);
          const ease = 1 - Math.pow(1 - p, 3);
          setCount(Math.floor(ease * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navBg = `rgba(3,3,12,${Math.min(scrollY / 80, 1) * 0.95})`;
  const navBorder = scrollY > 60 ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent';

  const features = [
    { icon: '📧', color: '#ffb347', title: 'Emails intelligents',  delay: '0s',   desc: 'Lis, résume et répond à tes emails automatiquement. Gmail, Outlook, Yahoo — tout en un.' },
    { icon: '📅', color: '#b388ff', title: 'Agenda connecté',      delay: '0.1s', desc: 'Crée, modifie et supprime des événements Google Calendar. Rappels vocaux 10 min avant.' },
    { icon: '✅', color: '#00d4aa', title: 'Gestion des tâches',   delay: '0.2s', desc: 'Organise ta to-do list avec priorités, catégories, rappels et alarmes intelligentes.' },
    { icon: '🎙', color: '#00b4ff', title: 'Vocal en malgache',    delay: '0.3s', desc: 'Parle à TeckiA en français ou en malgache. TTS haute qualité, transcription Groq.' },
    { icon: '🤖', color: '#ff4f6d', title: 'IA multi-moteurs',     delay: '0.4s', desc: 'Groq llama-3.3-70b + Gemini en fallback. Toujours disponible, toujours intelligent.' },
    { icon: '🔒', color: '#2ecc71', title: 'Sécurité maximale',    delay: '0.5s', desc: 'OAuth Google sécurisé. Tes données restent les tiennes. Chiffrement de bout en bout.' },
  ];

  const steps = [
    { num: '01', title: 'Télécharge TeckiA',      desc: 'Disponible sur Google Play Store. Crée ton compte en quelques secondes.', color: '#00d4aa' },
    { num: '02', title: 'Connecte tes comptes',    desc: 'Lie ton Gmail et Google Calendar en un clic via OAuth sécurisé.', color: '#00b4ff' },
    { num: '03', title: 'Parle à TeckiA',          desc: 'Maintiens le micro et dis ce que tu veux. TeckiA comprend et agit immédiatement.', color: '#b388ff' },
  ];

  const stats = [
    { val: 200,  suffix: '+',  label: 'Utilisateurs actifs', color: '#00d4aa' },
    { val: 98,   suffix: '%',  label: 'Satisfaction client',  color: '#00b4ff' },
    { val: 5,    suffix: '',   label: 'Langues supportées',   color: '#b388ff' },
    { val: 24,   suffix: '/7', label: 'Disponibilité',        color: '#ffb347' },
  ];

  return (
    <div className="teckia-landing" style={{ background: '#03030c', color: '#fff', minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{GLOBAL_CSS}</style>

      {/* ── NAV ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '16px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: navBg, backdropFilter: scrollY > 40 ? 'blur(20px)' : 'none',
        borderBottom: navBorder, transition: 'all 0.3s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg,#00d4aa,#00836b)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 900, color: '#fff',
          }}>✦</div>
          <span style={{ fontSize: 20, fontWeight: 900, letterSpacing: -0.5 }}>
            Tecki<span style={{ color: '#00d4aa' }}>A</span>
          </span>
        </div>

        <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <a href="#features" className="nav-link">Fonctionnalités</a>
          <a href="#how"      className="nav-link">Comment ça marche</a>
          <a href="/privacy"  className="nav-link">Confidentialité</a>
        </div>

        <button
          onClick={() => navigate('/login')}
          style={{
            background: 'linear-gradient(135deg,#00d4aa,#00836b)',
            color: '#fff', fontWeight: 800, fontSize: 14,
            padding: '10px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,212,170,0.3)', transition: 'all 0.2s ease',
          }}>
          Se connecter →
        </button>
      </nav>

      {/* ── HERO ── */}
      <section style={{
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '120px 24px 80px',
        position: 'relative',
      }}>
        {/* Glow */}
        <div style={{
          position: 'absolute', top: '30%', left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 700, height: 700,
          background: 'radial-gradient(circle,rgba(0,212,170,0.13) 0%,transparent 70%)',
          pointerEvents: 'none',
        }} />
        {/* Grid */}
        <div style={{
          position: 'absolute', inset: 0, overflow: 'hidden',
          backgroundImage: 'linear-gradient(rgba(0,212,170,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,212,170,0.04) 1px,transparent 1px)',
          backgroundSize: '80px 80px',
          animation: 'gridScroll 8s linear infinite',
          pointerEvents: 'none',
        }} />
        {/* Rings */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}>
          {[320, 480, 640].map((s, i) => (
            <div key={i} style={{
              position: 'absolute', width: s, height: s, borderRadius: '50%',
              border: `1px solid rgba(0,212,170,${0.06 - i * 0.015})`,
              top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              animation: `spin360 ${20 + i * 10}s linear infinite`,
            }} />
          ))}
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 860 }}>
          <div className="badge" style={{ marginBottom: 28, animation: 'heroIn 0.9s ease both' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#00d4aa', animation: 'pulse2 2s ease infinite', display: 'inline-block' }} />
            Assistant IA · Disponible sur Android
          </div>

          <h1 className="hero-title" style={{ marginBottom: 28 }}>
            L&apos;assistant IA qui<br />
            <span className="gradient-text">gère tout pour toi</span>
          </h1>

          <p className="hero-sub" style={{ maxWidth: 620, margin: '0 auto 48px' }}>
            TeckiA lit tes emails, gère ton agenda, crée tes tâches et te parle vocalement —
            en français et en malgache. Un assistant personnel intelligent pour Madagascar et au-delà.
          </p>

          <div className="hero-actions">
            <button className="cta-primary" onClick={() => navigate('/login')}>
              ✦ Commencer gratuitement
            </button>
            <a href="https://play.google.com/store" className="cta-outline" target="_blank" rel="noreferrer">
              ↓ Télécharger sur Android
            </a>
          </div>

          {/* Social proof */}
          <div style={{ marginTop: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, animation: 'heroIn 0.9s ease 0.7s both' }}>
            <div style={{ display: 'flex' }}>
              {['🇲🇬', '🇫🇷', '🇪🇺'].map((f, i) => (
                <div key={i} style={{
                  width: 34, height: 34, borderRadius: '50%',
                  border: '2px solid #03030c',
                  background: 'rgba(0,212,170,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginLeft: i === 0 ? 0 : -10, fontSize: 16,
                }}>{f}</div>
              ))}
            </div>
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, fontWeight: 600 }}>
              Utilisé par des équipes à Madagascar, France et dans le monde
            </span>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={{ padding: '60px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
          {stats.map((s, i) => (
            <div key={i} className="stat-card" style={{ animationDelay: `${i * 0.1}s` }}>
              <div style={{
                fontSize: 42, fontWeight: 900, color: s.color, marginBottom: 8,
                fontFamily: "'Space Grotesk',sans-serif",
              }}>
                <Counter target={s.val} suffix={s.suffix} />
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{ padding: '80px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div className="badge" style={{ marginBottom: 20 }}>Fonctionnalités</div>
          <h2 style={{
            fontFamily: "'Space Grotesk',sans-serif",
            fontSize: 'clamp(30px,5vw,52px)', fontWeight: 800, letterSpacing: -1, marginBottom: 16,
          }}>
            Tout ce dont tu as besoin,<br /><span className="gradient-text">dans une seule app</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 16, maxWidth: 520, margin: '0 auto' }}>
            TeckiA centralise toute ta productivité avec une IA qui comprend et agit.
          </p>
        </div>

        <div className="feat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
          {features.map((f, i) => (
            <div key={i} className="feat-card" style={{
              border: `1px solid ${f.color}33`,
              animationDelay: f.delay,
            }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg,transparent,${f.color},transparent)`,
              }} />
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: `${f.color}18`, border: `1px solid ${f.color}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, marginBottom: 20,
              }}>{f.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 10 }}>{f.title}</h3>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ padding: '80px 24px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div className="badge" style={{ marginBottom: 20 }}>Comment ça marche</div>
          <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(30px,5vw,52px)', fontWeight: 800, letterSpacing: -1 }}>
            Prêt en <span className="gradient-text">3 minutes</span>
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {steps.map((step, i) => (
            <div key={i} className="step-row" style={{
              border: `1px solid ${step.color}22`,
              animationDelay: `${i * 0.15}s`,
            }}>
              <div style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: 48, fontWeight: 900, color: `${step.color}33`,
                flexShrink: 0, lineHeight: 1, minWidth: 70,
              }}>{step.num}</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 6 }}>{step.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
              </div>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: `${step.color}18`, border: `1px solid ${step.color}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0, color: step.color,
              }}>→</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ padding: '80px 24px 120px', textAlign: 'center' }}>
        <div style={{
          maxWidth: 700, margin: '0 auto',
          background: 'linear-gradient(135deg,rgba(0,212,170,0.1),rgba(0,180,255,0.06))',
          border: '1px solid rgba(0,212,170,0.2)',
          borderRadius: 32, padding: '64px 40px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            width: 500, height: 500,
            background: 'radial-gradient(circle,rgba(0,212,170,0.08) 0%,transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: 56, marginBottom: 20, animation: 'floatY 3s ease infinite' }}>✦</div>
            <h2 style={{
              fontFamily: "'Space Grotesk',sans-serif",
              fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: -1, marginBottom: 16,
            }}>
              Commence dès maintenant
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16, marginBottom: 40, lineHeight: 1.6 }}>
              Rejoins des centaines d&apos;utilisateurs qui gèrent leur quotidien avec TeckiA.
              Gratuit pour commencer.
            </p>
            <div className="hero-actions">
              <button className="cta-primary" onClick={() => navigate('/login')}>
                ✦ Créer mon compte gratuit
              </button>
              <a href="https://play.google.com/store" className="cta-outline" target="_blank" rel="noreferrer">
                ↓ App Android
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '40px 32px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: 'linear-gradient(135deg,#00d4aa,#00836b)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff',
          }}>✦</div>
          <span style={{ fontSize: 18, fontWeight: 900 }}>
            Tecki<span style={{ color: '#00d4aa' }}>A</span>
          </span>
        </div>

        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', justifyContent: 'center' }}>
          <a href="/privacy" className="footer-link">Politique de Confidentialité</a>
          <a href="/terms"   className="footer-link">CGU</a>
          <a href="mailto:contact@tdidigital.io" className="footer-link">Contact</a>
          <a href="https://tdidigital.io" className="footer-link" target="_blank" rel="noreferrer">TDI Digital</a>
        </div>

        <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12 }}>
          © 2026 TDI Digital LLC · Tous droits réservés
        </p>
      </footer>
    </div>
  );
}